import {
  Box,
  Button,
  CssBaseline,
  Grid,
  Stack,
  SwipeableDrawer,
  Typography,
} from '@mui/material';
import PermissionPopup from '../components/permissionPopup';
import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';
import {styled} from '@mui/material/styles';
import {SVG_MARKUPS} from '../constants';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import SliderWithDrawer from '../components/sliderWithDrawer';
import {grey} from '@mui/material/colors';
import {Global} from '@emotion/react';
import {useNavigate} from 'react-router-dom';

const drawerBleeding = 56; // Increased drawer bleeding

const Root = styled('div')(({theme}) => ({
  height: '100%',
  backgroundColor: '#2D2D2D',
  ...theme.applyStyles('dark', {
    backgroundColor: theme.palette.background.default,
  }),
}));

const StyledBox = styled('div')(({theme}) => ({
  ...theme.applyStyles('dark', {
    backgroundColor: grey[800],
  }),
}));

const LeftRightHeadings = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  color: '#fff', // White color for the text
  fontWeight: 'bold',
  cursor: 'pointer',
  width: 'inherit',
  '& > :last-child': {
    marginRight: '20px',
  },
});

const Home = props => {
  const {window: windowFun} = props;
  const [tilted, setTilted] = useState(false);
  const [paused, setPaused] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const pausedRef = useRef(paused);
  const tiltedRef = useRef(tilted);
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const timerRef = useRef(null);
  const initialOrientation = useRef(null);
  const [open, setOpen] = useState(false);
  // const [svgMarkup, setSvgMarkup] = useState(SVG_MARKUPS.one);

  const [openPopup, setOpenPopup] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState();
  const [currentSvgKey, setCurrentSvgKey] = useState('four');
  const [alpha, setAlpha] = useState();
  const [isFullScreen, setIsFullScreen] = useState(false);

  const toggleDrawer = newOpen => () => {
    setOpen(newOpen);
  };

  // This is used only for the example
  const container =
    windowFun !== undefined ? () => windowFun().document.body : undefined;

  const handleOrientation = event => {
    const {alpha, beta, gamma} = event;

    // Adjust alpha for Android vs iOS
    let adjustedAlpha = alpha;

    if (window.screen.orientation && window.screen.orientation.angle) {
      adjustedAlpha = alpha + window.screen.orientation.angle;
    }

    // Keep alpha within 0-360 range
    if (adjustedAlpha < 0) adjustedAlpha += 360;
    if (adjustedAlpha >= 360) adjustedAlpha -= 360;

    console.log(
      `Adjusted alpha: ${adjustedAlpha}, beta: ${beta}, gamma: ${gamma}`,
    );

    // Existing logic remains unchanged
    if (pausedRef.current) {
      console.log('Orientation event ignored due to pause');
      return;
    }

    if (!initialOrientation.current) {
      initialOrientation.current = {alpha: adjustedAlpha, beta, gamma};
      return;
    }

    if (!tiltedRef.current) {
      setCurrentSvgKey('two');
    }

    setAlpha(adjustedAlpha);
  };

  const max_count = 20;
  const interval_ms = 30;
  const getRandomAlphanumeric = () => {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return chars.charAt(Math.floor(Math.random() * chars.length));
  };

  const animateText = (element, newText) => {
    if (element.initialtext) {
      element.count++;
      const randomStr = [...element.initialtext]
        .map(() => getRandomAlphanumeric())
        .join('');
      element.animatedStr = [...element.initialtext]
        .map((char, i) =>
          element.animatedStr[i] === char ? char : randomStr[i],
        )
        .join('');

      element.textContent =
        element.count < max_count ? element.animatedStr : element.initialtext;

      if (element.textContent !== element.initialtext) {
        setTimeout(() => animateText(element, newText), interval_ms);
      } else {
        delete element.count;
        delete element.initialtext;
        delete element.animatedStr;
      }
    } else {
      element.count = 0;
      element.initialtext = newText;
      element.animatedStr = ' '.repeat(newText.length);
      element.innerHTML = '';
      animateText(element, newText);
    }
  };
  const requestPermission = async () => {
    if (
      typeof DeviceMotionEvent !== 'undefined' &&
      typeof DeviceOrientationEvent.requestPermission === 'function'
    ) {
      try {
        const permissionState =
          await DeviceOrientationEvent.requestPermission();
        console.log('Permission state:', permissionState); // Log the permission state
        if (permissionState === 'granted') {
          setCurrentSvgKey('one');
          setTilted(false);
          setPaused(false);
          window.addEventListener('deviceorientation', handleOrientation);
          console.log('Device orientation listener added');
        } else {
          setPaused(true);
          setCurrentSvgKey('four');
          console.error('Permission not granted for Device Orientation');
        }
      } catch (error) {
        throw error;
      }
    } else {
      setCurrentSvgKey('one');
      setTilted(false);
      setPaused(false);
      window.addEventListener('deviceorientation', handleOrientation);
      console.log('Device orientation listener added (no permission needed)');
    }
  };

  const checkPermission = async () => {
    if (
      typeof DeviceOrientationEvent !== 'undefined' &&
      typeof DeviceOrientationEvent.requestPermission === 'function'
    ) {
      try {
        // Check if permission has already been granted
        const permissionState =
          await DeviceOrientationEvent.requestPermission();
        if (permissionState === 'granted') {
          return true;
        } else if (permissionState === 'denied') {
          setPermissionDenied(true);
          return false;
        } else {
          return false;
        }
      } catch (error) {
        console.error('Error checking permission', error);
      }
    } else {
      // For browsers where permission isn't required
      return true;
    }
  };

  const handleSvgClick = async () => {
    console.log('SVG clicked.');
    // Check for permission
    const hasPermission = await checkPermission();
    if (hasPermission) {
      console.log('insidepermi');
      setPaused(false);
      setTilted(false);
      setCurrentSvgKey('one');
      window.addEventListener('deviceorientation', handleOrientation);
    } else {
      // setOpenPopup(true);
      if (permissionDenied) {
        setOpenPopup(true);
      } else {
        handleAllowClick();
      }
    }
    toggleFullScreen();
  };
const renderSvg = () => {
  const containerStyle = {
    width: '100%',
    height: '400px',
    display: 'flex',
    alignItems: 'flex-start', 
    justifyContent: 'center',
    background: 'transparent',
    overflow: 'hidden',
  };

  const imgStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'contain', 
    objectPosition: 'top', 
    display: 'block',
    cursor: currentSvgKey === 'four' ? 'pointer' : 'default',
  };

  if (currentSvgKey === 'four') {
    return (
      <div style={containerStyle}>
        <img
          src="https://d2atk76x06g5eh.cloudfront.net/novaboard/assets/Frame+1000008426.png"
          alt="frame"
          style={imgStyle}
          onClick={handleSvgClick}
        />
      </div>
    );
  }

  if (currentSvgKey === 'two') {
    return (
      <div style={containerStyle}>
        <img
          src="https://d2atk76x06g5eh.cloudfront.net/novaboard/assets/Frame+1000008426.png"
          alt="frame"
          style={imgStyle}
        />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <img
        src="https://d2atk76x06g5eh.cloudfront.net/novaboard/assets/Frame+1000008426.png"
        alt="frame"
        style={imgStyle}
      />
    </div>
  );
};
  console.log(paused);
  const stopCountdown = () => {
    console.log(timerRef.current);
    initialOrientation.current = null;
    setTilted(false);
    setPaused(true);
    clearInterval(timerRef.current);
    setCurrentSvgKey('four');
    setCountdown(5);
  };

  const handleAllowClick = () => {
    setOpenPopup(false);
    requestPermission();
  };

  const handleSkipClick = () => {
    setCurrentSvgKey('four');
    setPaused(true);
    setOpenPopup(false);
  };

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      // Enter fullscreen
      document.documentElement.requestFullscreen?.() ||
        document.documentElement.mozRequestFullScreen?.() ||
        document.documentElement.webkitRequestFullscreen?.() ||
        document.documentElement.msRequestFullscreen?.();
    } else {
      // Exit fullscreen
      document.exitFullscreen?.() ||
        document.mozCancelFullScreen?.() ||
        document.webkitExitFullscreen?.() ||
        document.msExitFullscreen?.();
    }
    setIsFullScreen(!isFullScreen);
  };

  useEffect(() => {
    const elements = document.querySelectorAll('.animated');
    elements.forEach(elm => animateText(elm, elm.textContent));
    if (
      typeof DeviceOrientationEvent !== 'undefined' &&
      typeof DeviceOrientationEvent.requestPermission === 'function'
    ) {
      DeviceOrientationEvent.requestPermission()
        .then(permissionState => {
          if (permissionState === 'granted') {
            window.addEventListener('deviceorientation', handleOrientation);
          } else {
            console.error('Permission not granted for Device Orientation');
          }
        })
        .catch(error => {
          console.error(
            'Error requesting permission for Device Orientation',
            error,
          );
        });
    } else {
      // For other browsers or iOS versions that don't require permission
      window.addEventListener('deviceorientation', handleOrientation);
    }

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, [tilted]);

  useEffect(() => {
    if (tilted) {
      setCountdown(5); // Start countdown from 5
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev === 1) {
            clearInterval(timerRef.current); // Clear the timer
            window.location.href = 'https://uvastro.testserver2.co.in/';
            return 0;
          }
          return prev - 1;
        });
      }, 1000); // Countdown every second
    }

    return () => clearInterval(timerRef?.current); // Clean up the timer on unmount
  }, [tilted]);

  useEffect(() => {
    const handlePermission = async () => {
      try {
        const permission = await checkPermission();

        // if (permission) {
        //   setCurrentSvgKey('one');
        //   setPaused(false);
        //   // Add the event listener only if permission is granted
        //   window.addEventListener('deviceorientation', handleOrientation);
        // } else {
        //   // Show permission denied popup after delay
        //   setTimeout(() => {
        //     setCurrentSvgKey('four');
        //     setPaused(true);
        //     setOpenPopup(true);
        //     console.log('Permission not granted');
        //   }, 3000);
        // }
      } catch (error) {
        console.error('Error checking permission:', error);
      }
    };

    handlePermission();

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, []);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    tiltedRef.current = tilted;
  }, [tilted]);
  return (
    <>
      <Grid
        container
        justifyContent="space-evenly"
        fontFamily="Klapt"
        marginTop="30px"
      >
        <Grid item xs={2}></Grid>
        <Grid item xs={8} display="center" justifyContent="center">
          <img
            style={{height: '25px'}}
            src={`${process.env.REACT_APP_CDN}/1000007528.png`}
            alt="grey"
          />
        </Grid>
        <Grid item xs={2} textAlign="center">
          <img
            src={`${process.env.REACT_APP_CDN}/1000007537.png`}
            alt="grey"
            style={{height: '25px', cursor: 'pointer'}}
            onClick={() => {
              setOpenDrawer(true);
            }}
          />
        </Grid>
        {/* <Grid
          item
          xs={4}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'baseline',
          }}
        >
          <IconButton
        onClick={toggleFullScreen}
        color="primary"
        style={{padding: '0', color: '#E10004'}}
      >
        {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton> 
        </Grid> */}
      </Grid>
      <Grid
        container
        justifyContent="space-evenly"
        fontFamily="Klapt"
        marginTop="50px"
      >
        <Grid item xs={4}>
          <Box
            sx={{
              position: 'relative',
              padding: '15px',
              display: 'flex',
              alignItems: 'center',
              height: '65%',
              width: '150px',
              overflow: 'hidden', // To hide the overflowing text during the animation
              transformOrigin: 'center left', // Make sure the transform happens from the left-center
              animation: 'border-stretch 1s ease-out forwards', // Faster border animation
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: '2px',
                backgroundColor: 'white',
                transform: 'scaleY(0)', // Start with the border collapsed
                animation: 'border-stretch 1s ease-out forwards', // Faster border animation
              }}
            />

            <Typography
              variant="body1"
              style={{
                fontWeight: '700',
                fontSize: '12px',
                color: '#DEE0E1',
                textAlign: 'left',
                fontFamily: 'Klapt',
                lineHeight: '18px',
                letterSpacing: '1px',
                transform: 'translateX(-100%)', // Start off-screen to the left
                animation: 'text-slide 1s ease-out forwards 0.5s', // Faster text animation with a slight delay
              }}
            >
              CC<span style={{fontFamily: 'DisketMono'}}>-</span>12 <br />
              ONBOARD
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={2}></Grid>
        <Grid item xs={6}>
          {/* <Typography
            variant="body1"
            style={{
              fontWeight: '700',
              fontSize: '12px',
              color: '#DEE0E1',
              textAlign: 'end',
              fontFamily: 'Klapt',
              lineHeight: '18px',
              letterSpacing: '1px',
              transform: 'translateX(-100%)',
              animation: 'text-slide 1s ease-out forwards 0.5s',
            }}
          >
            UV SPACE COMMAND
            <br />
            DESTN: 481<span style={{fontFamily: 'DisketMono'}}>-</span>9S
          </Typography> */}
        </Grid>
        {/* <Grid item xs={4} style={{textAlign: 'center'}}>
          <img
            src="{`${process.env.REACT_APP_CDN}/1000007561.png`}"
            alt="Description of SVG"
            style={{
              width: 'auto',
              height: '30px',
              display: 'none',
            }}
            onClick={() => {
              window.location.href = 'https://uvastro.testserver2.co.in/';
            }}
          />
        </Grid> */}
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          {/* <Box
            sx={{
              padding: '0px 15px 0px 15px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <Typography
              variant="body1"
              style={{
                fontWeight: '700',
                fontSize: '12px',
                fontFamily: 'DisketMono',
                color: '#757676',
                textAlign: 'left',
                lineHeight: '18px',
                letterSpacing: '1px',
                transform: 'translateX(-100%)', // Start off-screen to the left
                animation: 'text-slide 1s ease-out forwards', // Slide-in animation for the first text
              }}
            >
              ARMED
            </Typography>
            <Typography
              variant="body1"
              style={{
                fontWeight: '700',
                fontSize: '12px',
                fontFamily: 'DisketMono',
                color: '#BE1E2D',
                textAlign: 'left',
                lineHeight: '18px',
                letterSpacing: '1px',
                transform: 'translateX(-100%)', // Start off-screen to the left
                animation: 'text-slide 1s ease-out forwards 0.5s', // Slide-in animation for the second text with a delay
              }}
            >
              ON
            </Typography>
          </Box> */}
          <div style={{width: '100%', overflow: 'hidden'}}>
            {/* <div
          style={{transform: 'scale(1.5)', transformOrigin: 'center'}}
          dangerouslySetInnerHTML={{__html: svgMarkup}}
        /> */}
            {renderSvg()}
          </div>
        </Grid>
      </Grid>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          color: '#fff',
          paddingBottom: '100px',
          marginTop: '30px',
        }}
      >
        {/* <Grid container> */}
          {/* <Grid item xs={6}>
            <img
              src={`${process.env.REACT_APP_CDN}/1000007537.png`}
              alt="grey"
              style={{height: '30px'}}
            />
          </Grid>
          <Grid item xs={6} textAlign="end">
            <img
              src={`${process.env.REACT_APP_CDN}/Spotify_icon.png`}
              alt="grey"
              style={{height: '30px'}}
            />
          </Grid> */}

          {/* {tilted && !paused && (
            <Grid item xs={12}>
              <Typography
                variant="body1"
                className="animated zoom-effect"
                style={{
                  fontWeight: '700',
                  fontSize: '12px',
                  color: '#BE1E2D',
                  fontFamily: 'Klapt',
                  textAlign: 'center',
                  letterSpacing: '1px',
                }}
              >
                TARGET LOCKED
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="center">
                <ArrowRightIcon
                  style={{
                    fontSize: '40px',
                    color: '#D9D9D9',
                    filter: 'drop-shadow(0 0 5px white)',
                  }}
                />
                <Typography
                  variant="body1"
                  className="animated"
                  style={{
                    fontWeight: '700',
                    fontSize: '19px',
                    textAlign: 'center',
                    fontFamily: 'Klapt',
                    letterSpacing: '1px',
                    margin: '0px',
                  }}
                >
                  LAUNCHING GAME
                </Typography>
                <Typography variant="body1">
                  <span>(</span>
                  <span
                    style={{
                      fontWeight: '400',
                      fontSize: '18px',
                      textAlign: 'center',
                      fontFamily: 'DisketMonoRegular',
                      margin: '0px',
                    }}
                  >
                    {countdown}S
                  </span>
                  <span>)</span>
                </Typography>
                <ArrowLeftIcon
                  style={{
                    fontSize: '40px',
                    color: '#D9D9D9',
                    filter: 'drop-shadow(0 0 5px white)',
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                style={{
                  fontWeight: '700',
                  fontSize: '14px',
                  color: '#757575',
                  fontFamily: 'DisketMono',
                  textAlign: 'center',
                  letterSpacing: '1px',
                  marginTop: '15px',
                }}
                onClick={stopCountdown}
              >
                CANCEL
              </Typography>
            </Grid>
          )}
          {!tilted && !paused && (
            <Grid item xs={12}>
              <Typography
                variant="body1"
                className="animated zoom-effect"
                style={{
                  fontWeight: '700',
                  fontSize: '12px',
                  color: '#BE1E2D',
                  fontFamily: 'Klapt',
                  textAlign: 'center',
                  letterSpacing: '1px',
                }}
              >
                APPROACHING
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="center">
                <ArrowRightIcon
                  style={{
                    fontSize: '40px',
                    color: '#D9D9D9',
                    filter: 'drop-shadow(0 0 5px white)',
                  }}
                />
                <Typography
                  variant="body1"
                  className="animated"
                  style={{
                    fontWeight: '700',
                    fontSize: '19px',
                    textAlign: 'center',
                    fontFamily: 'Klapt',
                    letterSpacing: '1px',
                    margin: '0px',
                  }}
                >
                  EVENT HORIZON
                </Typography>

                <ArrowLeftIcon
                  style={{
                    fontSize: '40px',
                    color: '#D9D9D9',
                    filter: 'drop-shadow(0 0 5px white)',
                  }}
                />
              </Box>
            </Grid>
          )}
          {!tilted && paused && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="center">
                <ArrowRightIcon
                  style={{
                    fontSize: '40px',
                    color: '#D9D9D9',
                    filter: 'drop-shadow(0 0 5px white)',
                  }}
                />
                <Typography
                  variant="body1"
                  className="animated"
                  style={{
                    fontWeight: '700',
                    fontSize: '20px',
                    textAlign: 'center',
                    fontFamily: 'Klapt',
                    letterSpacing: '1px',
                    margin: '0px',
                  }}
                >
                  Paused
                </Typography>
                <ArrowLeftIcon
                  style={{
                    fontSize: '40px',
                    color: '#D9D9D9',
                    filter: 'drop-shadow(0 0 5px white)',
                  }}
                />
              </Box>
            </Grid>
          )}
          {!tilted && (
            <Grid
              item
              xs={12}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{height: '55px', margin: '10px'}}
            >
              <div className="plus-sign">
                <div className="line horizontal"></div>
                <div className="line vertical"></div>
              </div>
            </Grid>
          )}
        </Grid> */}
        <Grid item xs={12}>
          <Box
            sx={{
              width: '100%',
              backgroundImage: `url("${process.env.REACT_APP_CDN}/airspaceBgcard.png")`,
              color: '#fff',
              position: 'relative',
              zIndex: 10,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <Grid container>
              <Grid item xs={6} sx={{padding: '0', margin: '0'}}>
                <img
                  src={`${process.env.REACT_APP_CDN}/futuristicAttire.png`}
                  alt="grey"
                  style={{width: '100%', height: 'auto'}}
                />
              </Grid>
              <Grid item xs={6} display="flex" alignItems="center">
                <Stack>
                  <Typography
                    variant="body1"
                    style={{
                      fontWeight: '700',
                      fontSize: '19px',
                      fontFamily: 'Klapt',
                      lineHeight: '1',
                    }}
                  >
                    EXPLORE
                    <br />
                    CAPSULE
                  </Typography>
                  <Typography
                    variant="body1"
                    style={{
                      fontWeight: '400',
                      fontSize: '12px',
                      fontFamily: 'DisketMonoRegular',
                      marginTop: '15px',
                      letterSpacing: '1px',
                      lineHeight: '2',
                    }}
                  >
                    ELEVATE YOUR STYLE <br /> WITH TECH-ENABLED
                    <br /> APPAREL
                  </Typography>
                  <Button
                    sx={{
                      borderLeft: '1px solid #FFFFFF',
                      borderRight: '1px solid #FFFFFF',
                      borderBottom: '1px solid #FFFFFF',
                      backgroundColor: 'transparent',
                      color: 'white',
                      position: 'relative',
                      marginTop: '45px',
                      padding: '15px',
                      width: '85%',
                      borderRadius: 0,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '25%',
                        borderTop: '1px solid #FFFFFF',
                      },
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      fontFamily: 'DisketMono',
                    }}
                    onClick={() => {
                      window.location.href =
                        'https://uvairspace.myshopify.com/';
                    }}
                  >
                    SHOP NOW
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
          <Grid
            item
            xs={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{height: '55px', margin: '10px 0'}}
          >
            <div className="plus-sign">
              <div className="line horizontal"></div>
              <div className="line vertical"></div>
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item xs={12} sm={8} md={6}>
            <Box display="flex" justifyContent="center" alignItems="center">
              <iframe
                style={{borderRadius: '12px'}}
                src="https://open.spotify.com/embed/playlist/7vGDpTPlCl6IV6YnI5MTh3?utm_source=generator&theme=0"
                width="100%"
                height="352"
                frameBorder="0"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              ></iframe>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Root>
        <CssBaseline />
        <Global
          styles={{
            '.MuiDrawer-root > .MuiPaper-root': {
              height: `calc(50% - ${drawerBleeding}px)`, // Adjust height calculation based on new bleeding
              overflow: 'visible',
            },
          }}
        />

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            margin: '0 auto',
          }}
        >
          <SwipeableDrawer
            container={container}
            anchor="bottom"
            open={open}
            onClose={toggleDrawer(false)}
            onOpen={toggleDrawer(true)}
            swipeAreaWidth={drawerBleeding}
            disableSwipeToOpen={false}
            ModalProps={{
              keepMounted: true,
            }}
          >
            <StyledBox
              sx={{
                position: 'absolute',
                top: -drawerBleeding,
                visibility: 'visible',
                width: '100%', // Ensures the header box takes full width
              }}
            >
              <Box
                sx={{
                  position: 'relative', // Make this Box relative for proper positioning
                  width: 'inherit', // Keep the width the same as the image
                  height: '60px', // Same height as the image
                }}
              >
                <img
                  src={`${process.env.REACT_APP_CDN}/grey.png`}
                  alt="grey"
                  style={{
                    width: 'inherit',
                    height: '60px',
                    position: 'absolute', // Set image to absolute to act as background
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  }}
                />
                <LeftRightHeadings
                  onClick={toggleDrawer(true)}
                  sx={{
                    position: 'absolute', // Position the headings on top of the image
                    top: '40%',
                    left: 0,
                    right: 0,
                    transform: 'translateY(-50%)', // Center vertically
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                    zIndex: 1, // Ensure headings stay above the image
                  }}
                >
                  <Typography
                    variant="body1"
                    style={{
                      fontWeight: '700',
                      fontSize: '12px',
                      fontFamily: 'Gemsbuck01Bold',
                      color: '#DEE0E1',
                      letterSpacing: '1px',
                    }}
                  >
                    MISSION DATA
                  </Typography>
                  <Typography
                    variant="body1"
                    style={{
                      fontWeight: '700',
                      fontSize: '12px',
                      color: '#DEE0E1',
                      fontFamily: 'Gemsbuck01Bold',
                      letterSpacing: '1px',
                    }}
                  >
                    GEN. 2077
                  </Typography>
                </LeftRightHeadings>
              </Box>
            </StyledBox>
            <StyledBox
              sx={{
                px: 2,
                pb: 2,
                height: '100%',
                overflow: 'auto',
                backgroundColor: '#2D2D2D', // Dark background for the content
                // White text for the content
              }}
            >
              <img
                src={`${process.env.REACT_APP_CDN}/missionData.png`}
                alt="missionData"
                style={{
                  width: '100%',
                  marginTop: '10px',
                }}
              />
              <Typography
                variant="body1"
                style={{
                  fontFamily: 'DisketMonoRegular',
                  color: '#FFFFFF',
                  fontSize: '12px',
                  marginTop: '12px',
                }}
              >
                ABOARD THE MOTHERSHIP, THEIR NEW HOME FOR YEARS TO COME, THEY
                EXPLORE DISTANT STAR SYSTEMS AND PUSH THE BOUNDARIES OF HUMAN
                KNOWLEDGE TO ACHIEVE MULTI-PLANETARY STATUS.
              </Typography>

              <Typography
                variant="body1"
                style={{
                  fontFamily: 'DisketMonoRegular',
                  color: '#FFFFFF',
                  fontSize: '12px',
                  marginTop: '13px',
                }}
              >
                As they navigate hazardous labs and develop groundbreaking
                technologies, they are aided by advanced, non-human companions,
                who become essential members of the crew, bringing unique
                perspectives and capabilities to their journey.
              </Typography>
            </StyledBox>
          </SwipeableDrawer>
        </Box>

        <SliderWithDrawer
          openDrawer={openDrawer}
          setOpenDrawer={setOpenDrawer}
        />
      </Root>
      <PermissionPopup
        open={openPopup}
        onClose={handleSkipClick}
        onAllow={handleAllowClick}
        permissionDenied={permissionDenied}
      />
    </>
  );
};

Home.propTypes = {
  window: PropTypes.func,
};

export default Home;
